"use client";

import { isArray } from "lodash";
import { Loader2, Trash2 } from "lucide-react";
import { useRef, useState } from "react";
import toast from "react-hot-toast";
import { twMerge } from "tailwind-merge";

import { DragUpload, DragUploadRef } from "@/components/custom/drag-upload";
import { Button } from "@/components/ui/button";
import { documentService } from "@/contracts";
import { useIsMobile } from "@/hooks/use-mobile";
import { generateHashFromFile } from "@/lib/utils";
import moment from "moment";
import { userApi } from "@/apis";
import { If } from "@/components/custom/condition";

enum EVerifiedStatus {
  pending = "pending",
  verified = "verified",
  notMatch = "not-match",
  failed = "failed",
}

interface VerifiedFile {
  id: string;
  filename: string;
  size: number;
  status: EVerifiedStatus;
  originalFile: File;
  ownerName: string;
  time: string;
}

export default function ComparePage() {
  const { isMobile } = useIsMobile(729);

  const uploadRef = useRef<DragUploadRef>(null);

  const [selectedFiles, setSelectedFiles] = useState<VerifiedFile[]>([]);
  const [isVerifying, setIsVerifying] = useState(false);
  const [hasVerified, setHasVerified] = useState(false);

  const formatFileSize = (bytes: number) => {
    if (bytes === 0) return "0 Bytes";
    const k = 1024;
    const sizes = ["Bytes", "KB", "MB", "GB"];
    const i = Math.floor(Math.log(bytes) / Math.log(k));
    return (
      Number.parseFloat((bytes / Math.pow(k, i)).toFixed(2)) + " " + sizes[i]
    );
  };

  const handleChangeFiles = (files: File[]) => {
    if (!isArray(files)) return;

    let tempSelectedFiles = selectedFiles;

    if (hasVerified) {
      setHasVerified(false);
      setSelectedFiles([]);
      tempSelectedFiles = [];
      uploadRef.current?.reset();
    }

    const newFiles: VerifiedFile[] = [];
    const existingFileNames = new Set(tempSelectedFiles.map((f) => f.filename));

    files.forEach((file) => {
      newFiles.push({
        id: Math.random().toString(36).substr(2, 9),
        filename: file.name,
        size: file.size,
        status: EVerifiedStatus.pending,
        originalFile: file,
        ownerName: "",
        time: moment().format("DD MMM, YYYY"),
      });
      existingFileNames.add(file.name);
    });

    setSelectedFiles((files) => [...files, ...newFiles]);
  };

  const removeFile = (id: string) => {
    setSelectedFiles((files) => files.filter((file) => file.id !== id));
  };

  const verifyDocument = async (document: VerifiedFile) => {
    try {
      const hash = await generateHashFromFile(document.originalFile);

      const folderId = await documentService.getFolderByHash(hash);

      let status = EVerifiedStatus.notMatch;
      let ownerName = "";

      if (folderId) {
        const folder = await documentService.getFolder(folderId);

        if (folder.folderOwner) {
          const user = await userApi.getSystemUserByWallet(
            folder.folderOwner.toLowerCase()
          );

          if (user.name) ownerName = user.name;
        }

        status = EVerifiedStatus.verified;
      }

      return { ...document, status, ownerName };
    } catch (error) {
      return { ...document, status: EVerifiedStatus.failed };
    }
  };

  const verifyDocuments = async () => {
    if (!selectedFiles.length) return;

    setIsVerifying(true);
    setHasVerified(true);

    try {
      const results = await Promise.all(selectedFiles.map(verifyDocument));
      setSelectedFiles(results);
    } catch (err) {
      toast.error("Có lỗi xảy ra khi xác thực tài liệu");
    } finally {
      setIsVerifying(false);
      uploadRef.current?.reset();
    }
  };

  const getVerifiedIcon = (file: VerifiedFile) => {
    if (isVerifying && !isMobile) {
      return (
        <Loader2
          strokeWidth={2}
          className="w-6 h-6 animate-spin text-[#3B82F6]"
        />
      );
    }

    if (file.status === EVerifiedStatus.verified) {
      return <img src="/icon-check.svg" alt="verified" className="w-6 h-6" />;
    }

    const failedStatus = [EVerifiedStatus.notMatch, EVerifiedStatus.failed];

    if (failedStatus.includes(file.status)) {
      return <img src="/icon-x.svg" alt="failed" className="w-6 h-6" />;
    }
  };

  const getVerifiedText = (file: VerifiedFile) => {
    if (file.status === EVerifiedStatus.verified) {
      return (
        <div>
          <div className="text-[#33B76B] text-xs font-medium">
            Tài liệu này là bản gốc
          </div>
          <If
            condition={Boolean(file.ownerName)}
            Then={
              <div className="text-[10px] text-[#33B76B]">
                Được {file.ownerName} phát hành
              </div>
            }
          />
        </div>
      );
    }

    if (file.status === EVerifiedStatus.notMatch) {
      return (
        <div className="text-[#E83C3B] text-xs font-medium">
          Tài liệu này chưa được phát hành bởi hệ thống của chúng tôi
        </div>
      );
    }

    if (file.status === EVerifiedStatus.failed) {
      return (
        <div className="text-[#E83C3B] text-xs font-medium">
          Có lỗi khi xác thực tài liệu
        </div>
      );
    }
  };

  const renderFileCard = (file: VerifiedFile) => {
    if (isMobile) {
      return (
        <div
          key={file.id}
          className={twMerge(
            "grid grid-cols-1 gap-2 py-2 px-3 bg-gray-50 rounded-lg",
            file.status === EVerifiedStatus.verified && "bg-[#EBFAF1]",
            [EVerifiedStatus.failed, EVerifiedStatus.notMatch].includes(
              file.status
            ) && "bg-[#FFEDED]"
          )}
        >
          <div className="flex items-center gap-2">
            <img src="/icon-file.svg" alt="file icon" className="w-6 h-6" />
            <div className="flex-1">
              <p className="font-semibold text-sm text-main">{file.filename}</p>

              <div className="flex items-center text-xs text-gray-500 gap-1">
                <p>{file.time}</p>
                <div>•</div>
                <p>{formatFileSize(file.size)}</p>
              </div>
            </div>
            {!isVerifying && file.status === EVerifiedStatus.pending && (
              <Button
                variant="ghost"
                size="sm"
                onClick={() => removeFile(file.id)}
              >
                <Trash2 className="w-4 h-4" />
              </Button>
            )}
            {isVerifying && (
              <Loader2
                strokeWidth={2}
                className="w-6 h-6 animate-spin text-[#3B82F6]"
              />
            )}
          </div>
          <div className="flex items-center gap-2">
            {getVerifiedIcon(file)}
            {getVerifiedText(file)}
          </div>
        </div>
      );
    }

    return (
      <div
        key={file.id}
        className={twMerge(
          "flex items-center gap-2 py-2 px-3 bg-gray-50 rounded-lg",
          file.status === EVerifiedStatus.verified && "bg-[#EBFAF1]",
          [EVerifiedStatus.failed, EVerifiedStatus.notMatch].includes(
            file.status
          ) && "bg-[#FFEDED]"
        )}
      >
        <img src="/icon-file.svg" alt="file icon" className="w-6 h-6" />
        <div className="flex-1">
          <p className="font-semibold text-sm text-main line-clamp-1">
            {file.filename}
          </p>

          <div className="flex items-center text-xs text-gray-500 gap-1">
            <p>{file.time}</p>
            <div>•</div>
            <p>{formatFileSize(file.size)}</p>
          </div>
        </div>
        <div className="flex items-center gap-2">
          {getVerifiedText(file)}
          {getVerifiedIcon(file)}
          {!isVerifying && file.status === EVerifiedStatus.pending && (
            <Button
              variant="ghost"
              size="sm"
              onClick={() => removeFile(file.id)}
            >
              <Trash2 className="w-4 h-4" />
            </Button>
          )}
        </div>
      </div>
    );
  };

  return (
    <main className="max-w-[800px] mx-auto">
      <div className="text-center mb-6 md:mb-8">
        <h2 className="text-2xl md:text-[32px] leading-none mb-2.5 font-bold">
          Xác thực tài liệu
        </h2>
        <p className="text-gray-500 md:text-base text-xs">
          Tải tài liệu lên để xác thực bản gốc bằng công nghệ blockchain – nhanh
          chóng, minh bạch và không thể giả mạo.
        </p>
      </div>

      <DragUpload
        ref={uploadRef}
        className="mb-4"
        onChange={handleChangeFiles}
      />

      {selectedFiles.length > 0 && (
        <div className="w-full overflow-y-auto overflow-x-hidden grid grid-cols-1 gap-2 pb-4 scrollbar-thin max-h-[500px] mb-8">
          {selectedFiles.map((file) => renderFileCard(file))}
        </div>
      )}

      {selectedFiles.length > 0 && (
        <div className="text-center">
          <Button
            onClick={verifyDocuments}
            disabled={
              isVerifying || selectedFiles.every((f) => f.status !== "pending")
            }
            className="bg-main text-white w-full"
            size="lg"
          >
            {isVerifying ? (
              <>
                <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                Đang xác thực...
              </>
            ) : (
              "Xác thực tài liệu"
            )}
          </Button>
        </div>
      )}
    </main>
  );
}
